import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import SyncIcon from "@material-ui/icons/Sync";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  pageCard: {
    borderRadius: 24,
    padding: theme.spacing(2.5),
    boxShadow: "0 18px 40px rgba(16, 63, 104, 0.08)"
  },
  tableWrap: {
    overflowX: "auto"
  },
  infoBox: {
    borderRadius: 18,
    padding: theme.spacing(2),
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(31,84,132,0.05)",
    marginBottom: theme.spacing(2)
  }
}));

const emptyForm = {
  id: null,
  code: "",
  name: "",
  baseUrl: "",
  companiesEndpoint: "",
  authToken: "",
  authType: "bearer",
  notes: "",
  isActive: true,
  canImportCompanies: true,
  canPushCompanies: false,
  canPushResolutions: false
};

const ClientSources = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.super || user?.profile === "admin";
  const [records, setRecords] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const loadRecords = async () => {
    try {
      const { data } = await api.get("/clients/sources");
      setRecords(Array.isArray(data) ? data : []);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadRecords();
    }
  }, [isAdmin]);

  const openCreateModal = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = record => {
    setForm({
      id: record.id,
      code: record.code || "",
      name: record.name || "",
      baseUrl: record.baseUrl || "",
      companiesEndpoint: record.companiesEndpoint || "",
      authToken: record.authToken || "",
      authType: record.authType || "bearer",
      notes: record.notes || "",
      isActive: record.isActive !== false,
      canImportCompanies: record.canImportCompanies !== false,
      canPushCompanies: record.canPushCompanies === true,
      canPushResolutions: record.canPushResolutions === true
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (form.id) {
        await api.put(`/clients/sources/${form.id}`, form);
        toast.success("Fuente actualizada.");
      } else {
        await api.post("/clients/sources", form);
        toast.success("Fuente creada.");
      }
      setModalOpen(false);
      await loadRecords();
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async sourceId => {
    try {
      setSyncingId(sourceId);
      const { data } = await api.post(`/clients/sources/${sourceId}/sync`);
      toast.success(
        `Sincronización completada. Importados: ${data?.importedCustomers || 0}, actualizados: ${data?.updatedCustomers || 0}.`
      );
      await loadRecords();
    } catch (error) {
      toastError(error);
    } finally {
      setSyncingId(null);
    }
  };

  if (!isAdmin) {
    return (
      <MainContainer>
        <MainHeader>
          <Title>Fuentes API de clientes</Title>
        </MainHeader>
        <Paper className={classes.pageCard}>
          <Typography>Acceso restringido a administradores.</Typography>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Fuentes API de clientes</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="outlined" color="primary" onClick={loadRecords}>
            Recargar
          </Button>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreateModal}>
            Nueva fuente
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.pageCard}>
        <Box className={classes.infoBox}>
          <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
            Administración de fuentes
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Desde aquí se configuran los endpoints externos, sus credenciales y la ejecución de sincronización.
          </Typography>
        </Box>

        <div className={classes.tableWrap}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fuente</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Última sincronización</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => (
                <TableRow key={record.id}>
                  <TableCell>
                    <Typography variant="subtitle2" style={{ fontWeight: 800 }}>{record.name}</Typography>
                    <Typography variant="caption" color="textSecondary">{record.code}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{record.baseUrl}</Typography>
                    <Typography variant="caption" color="textSecondary">{record.companiesEndpoint}</Typography>
                  </TableCell>
                  <TableCell>{record.isActive ? "Activa" : "Inactiva"}</TableCell>
                  <TableCell>
                    {record.lastSyncAt ? new Date(record.lastSyncAt).toLocaleString("es-CO") : "Sin ejecutar"}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gridGap={8}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() => openEditModal(record)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<SyncIcon />}
                        disabled={syncingId === record.id}
                        onClick={() => handleSync(record.id)}
                      >
                        {syncingId === record.id ? "Sincronizando..." : "Sincronizar"}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.id ? "Editar fuente" : "Nueva fuente"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth variant="outlined" label="Código" value={form.code} onChange={e => setForm(prev => ({ ...prev, code: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth variant="outlined" label="Nombre" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="Base URL" value={form.baseUrl} onChange={e => setForm(prev => ({ ...prev, baseUrl: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth variant="outlined" label="Endpoint de compañías" value={form.companiesEndpoint} onChange={e => setForm(prev => ({ ...prev, companiesEndpoint: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth variant="outlined" label="Token / credencial" value={form.authToken} onChange={e => setForm(prev => ({ ...prev, authToken: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} variant="outlined" label="Notas" value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))} />} label="Activa" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.canImportCompanies} onChange={e => setForm(prev => ({ ...prev, canImportCompanies: e.target.checked }))} />} label="Importa clientes" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.canPushCompanies} onChange={e => setForm(prev => ({ ...prev, canPushCompanies: e.target.checked }))} />} label="Empuja empresas" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.canPushResolutions} onChange={e => setForm(prev => ({ ...prev, canPushResolutions: e.target.checked }))} />} label="Empuja resoluciones" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ClientSources;
